import { useState, useEffect } from "react";
import {
  Box,
  Container,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { PageLayout } from "../../components/page/page-layout";
import { LogsListResults } from "../../components/logs-visualization/logs-list-results";
import { LogsListToolbar } from "../../components/logs-visualization/logs-list-toolbar";
import SearchIcon from "@mui/icons-material/Search";
import { searchGetLogs } from "../../services/LogsVisualizationService";

const LogsVisualization = () => {
  const today = new Date().toISOString().split("T")[0];
  const todayDate = new Date();

  const fiveDaysAgo = new Date(todayDate);
  fiveDaysAgo.setDate(todayDate.getDate() - 5);

  const formatDate = (date) => date.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(formatDate(fiveDaysAgo));
  const [endDate, setEndDate] = useState(formatDate(todayDate));
  const [nivel, setNivel] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [formError, setFormError] = useState("");
  const [emptyResults, setEmptyResults] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [dataTable, setDataTable] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLogs = async (pageToFetch = page, pageSizeToFetch = pageSize) => {
    setLoading(true);
    setFormError("");
    setEmptyResults("");

    if (searchTerm && searchTerm.length < 3) {
      setFormError("El término de búsqueda debe tener al menos 3 caracteres.");
      setLoading(false);
      return;
    }
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      setFormError("La fecha 'Desde' debe ser anterior o igual a 'Hasta'.");
      setLoading(false);
      return;
    }

    const params = {
      text: searchTerm,
      loglevel: nivel,
      from: startDate,
      to: endDate,
      page: pageToFetch,
      pageSize: pageSizeToFetch,
    };

    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([key, value]) => value !== "")
    );

    try {
      const res = await searchGetLogs(cleanParams);
      const results = res.data.results;

      setDataTable(results);
      setTotalPages(res.data.totalPages || res.totalPages);
      setTotalItems(res.data.totalItems || res.totalItems || results.length);

      if (results.length === 0) {
        setEmptyResults("No se encontraron resultados.");
      }
    } catch (err) {
      console.error("Error fetching logs:", err);
      setEmptyResults("Error al obtener los datos.");
      setDataTable([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  },[]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchLogs(newPage, pageSize);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
    fetchLogs(1, newPageSize);
  };

  const handleSearchClick = () => {
    setPage(1);
    fetchLogs(1, pageSize);
  };

  return (
    <PageLayout>
      <Box component="main" sx={{ flexGrow: 1, py: 2 }}>
        <Container maxWidth={false}>
          <LogsListToolbar />
          <Box sx={{ mt: 6 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                alignItems: { xs: "stretch", lg: "center" },
                gap: 2,
                flexWrap: "wrap",
                mb: 6,
              }}
            >
              <TextField
                label="Desde"
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (
                    formError &&
                    endDate &&
                    new Date(e.target.value) <= new Date(endDate)
                  )
                    setFormError("");
                }}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: today }}
                error={
                  !!formError &&
                  startDate &&
                  endDate &&
                  new Date(startDate) > new Date(endDate)
                }
                sx={{
                  width: { xs: "100%", lg: "auto" },
                  minWidth: 150,
                  flexGrow: { xs: 1, lg: 0 },
                }}
              />

              <TextField
                label="Hasta"
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  if (
                    formError &&
                    startDate &&
                    new Date(startDate) <= new Date(e.target.value)
                  )
                    setFormError("");
                }}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: today }}
                error={
                  !!formError &&
                  startDate &&
                  endDate &&
                  new Date(startDate) > new Date(endDate)
                }
                sx={{
                  width: { xs: "100%", lg: "auto" },
                  minWidth: 150,
                  flexGrow: { xs: 1, lg: 0 },
                }}
              />

              <FormControl
                size="small"
                sx={{
                  minWidth: 150,
                  width: { xs: "100%", lg: "auto" },
                  flexGrow: { xs: 1, lg: 0 },
                }}
              >
                <InputLabel id="nivel-label" shrink>
                  Nivel
                </InputLabel>
                <Select
                  labelId="nivel-label"
                  value={nivel}
                  onChange={(e) => setNivel(e.target.value)}
                  label="Nivel"
                  displayEmpty
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="Information">Información</MenuItem>
                  <MenuItem value="Warning">Aviso</MenuItem>
                  <MenuItem value="Error">Error</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Buscar"
                variant="outlined"
                size="small"
                sx={{
                  minWidth: 150,
                  width: { xs: "100%", lg: "auto" },
                  flexGrow: { xs: 1, lg: 0 },
                }}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  if (
                    formError &&
                    (e.target.value.length === 0 || e.target.value.length >= 3)
                  )
                    setFormError("");
                }}
                error={
                  !!formError && searchTerm.length > 0 && searchTerm.length < 3
                }
              />

              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearchClick}
                sx={{
                  height: 40,
                  width: { xs: "100%", lg: "auto" },
                  alignSelf: { xs: "stretch", lg: "center" },
                }}
              >
                Buscar
              </Button>
            </Box>

            <LogsListResults
              logsInfo={dataTable}
              loading={loading}
              emptyResults={!formError && emptyResults}
              currentPage={page}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handlePageSizeChange}
            />
          </Box>
        </Container>
      </Box>
    </PageLayout>
  );
};

export default LogsVisualization;
